import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { captureException, captureEvent, EVENTS } from '@/lib/posthog';
import { reportGleapError } from '@/lib/gleap';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);

    // Capture exception in PostHog
    captureException(error, 'ErrorBoundary', {
      componentStack: errorInfo.componentStack,
    });

    // Also capture as a custom event for easier tracking
    captureEvent(EVENTS.ERROR_BOUNDARY_TRIGGERED, {
      error_message: error.message,
      error_name: error.name,
      component_stack: errorInfo.componentStack,
    });

    reportGleapError(error, {
      component_stack: errorInfo.componentStack,
      source: 'ErrorBoundary',
    });
  }

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="text-center p-8">
            <AlertTriangle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2">Something went wrong</h1>
            <p className="text-muted-foreground mb-4">
              {this.state.error?.message || 'An unexpected error occurred'}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="text-primary hover:underline"
            >
              Refresh page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
